/**
 * Thin fetch wrapper for the captain SPA (web UI Phase 2 chunk 4/6).
 *
 * - Always sends `credentials: "include"` so the `nofuzz_session` cookie
 *   round-trips on every request. In v0.1 the SPA + Worker are
 *   same-origin so this is a no-op, but if Phase 2.x splits them across
 *   subdomains (e.g. `app.nofuzz.net` + `api.nofuzz.net`) the wrapper
 *   keeps the call sites unchanged.
 * - Throws `ApiError` on non-2xx so callers can branch on `.status`
 *   without re-checking `res.ok`.
 *
 * No retry / no caching. Both belong at the call site (or in a SWR-style
 * hook) where the right policy depends on the endpoint.
 */

export class ApiError extends Error {
  status: number;
  /** Parsed JSON body if the server returned one, otherwise null. */
  body: unknown;
  constructor(status: number, message: string, body: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function parseBody(res: Response): Promise<unknown> {
  // Try JSON; fall back to text. Empty body → null.
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  try {
    const text = await res.text();
    return text.length > 0 ? text : null;
  } catch {
    return null;
  }
}

function messageFromBody(body: unknown, fallback: string): string {
  if (body && typeof body === "object" && "error" in body) {
    const err = (body as { error?: unknown }).error;
    if (typeof err === "string" && err.length > 0) return err;
  }
  return fallback;
}

export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const body = await parseBody(res);
  if (!res.ok) {
    throw new ApiError(
      res.status,
      messageFromBody(body, `GET ${path} → ${res.status}`),
      body,
    );
  }
  return body as T;
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(body ?? {}),
  });
  const parsed = await parseBody(res);
  if (!res.ok) {
    throw new ApiError(
      res.status,
      messageFromBody(parsed, `POST ${path} → ${res.status}`),
      parsed,
    );
  }
  return parsed as T;
}
